/**
 * Phase 15 — threaded comments panel for a Story.
 *
 * Renders top-level comments with their nested replies, lets the
 * actor post new comments + replies, resolve threads, and (for own
 * comments) delete. Optional block-path scoping is exposed via a
 * read-only display of the block_path field — admins can pre-fill
 * it by selecting a block in the editor in a future increment.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import type { ManagementResult } from '@peacock/sdk-js';
import { makeApi } from '../../lib/api';
import { useAuthStore } from '../auth/authStore';

interface CommentsPanelProps {
  storyUuid: string;
}

function resultCode<T>(result: ManagementResult<T>): string {
  if (result.status === 'forbidden') return result.code;
  if (result.status === 'error') return `http-${result.code}`;
  return result.status;
}

export function CommentsPanel({ storyUuid }: CommentsPanelProps): JSX.Element | null {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [showResolved, setShowResolved] = useState(false);

  const enabled = token !== null && spaceSlug !== null;

  const commentsQuery = useQuery({
    enabled,
    queryKey: ['comments', spaceSlug, storyUuid, showResolved],
    queryFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.listComments(spaceSlug, storyUuid, showResolved);
      if (result.status !== 'ok') {
        throw new Error(resultCode(result));
      }
      return result.data;
    },
  });

  const postMutation = useMutation({
    mutationFn: async (input: { body: string; parentId?: number }) => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.postComment(spaceSlug, storyUuid, input.body, {
        parentId: input.parentId,
      });
      if (result.status !== 'ok') {
        throw new Error(resultCode(result));
      }
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', spaceSlug, storyUuid] });
      setDraft('');
      setReplyDraft('');
      setReplyToId(null);
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (commentId: number) => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.resolveComment(spaceSlug, storyUuid, commentId);
      if (result.status !== 'ok') {
        throw new Error(resultCode(result));
      }
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', spaceSlug, storyUuid] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (commentId: number) => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.deleteComment(spaceSlug, storyUuid, commentId);
      if (result.status !== 'ok') {
        throw new Error(resultCode(result));
      }
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', spaceSlug, storyUuid] });
    },
  });

  if (!enabled) {
    return null;
  }

  const comments = commentsQuery.data?.data ?? [];

  return (
    <aside
      data-testid="comments-panel"
      className="rounded-md border border-zinc-200 bg-white p-3"
    >
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
          Kommentare
        </h3>
        <label className="flex items-center gap-1 text-[11px] text-zinc-500">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            data-testid="comments-show-resolved"
            className="h-3 w-3"
          />
          Gelöste anzeigen
        </label>
      </header>

      {/* New top-level comment form */}
      <div className="mb-3 rounded border border-zinc-100 p-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="comment-draft-input"
          placeholder="Neuen Kommentar schreiben…"
          rows={2}
          maxLength={2000}
          className="w-full rounded border border-zinc-200 bg-zinc-50 p-1.5 text-xs"
        />
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            data-testid="comment-post-button"
            disabled={draft.trim().length === 0 || postMutation.isPending}
            onClick={() => postMutation.mutate({ body: draft.trim() })}
            className="rounded bg-zinc-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
          >
            Senden
          </button>
        </div>
      </div>

      {comments.length === 0 ? (
        <p className="text-[11px] italic text-zinc-500">Noch keine Kommentare.</p>
      ) : (
        <ul className="space-y-2">
          {comments.map((c) => (
            <li
              key={c.id}
              data-testid={`comment-${c.id}`}
              className={
                'rounded border p-2 text-xs ' +
                (c.resolved_at ? 'border-zinc-100 bg-zinc-50 opacity-70' : 'border-zinc-200')
              }
            >
              <header className="mb-1 flex items-center justify-between">
                <span className="text-[10px] text-zinc-500">
                  Token {c.actor_token_id ?? '?'} ·{' '}
                  {c.created_at ? new Date(c.created_at).toLocaleString('de-AT') : '?'}
                  {c.block_path ? (
                    <span className="ml-1 rounded bg-zinc-100 px-1 font-mono text-[9px]">
                      {c.block_path}
                    </span>
                  ) : null}
                </span>
                <span className="flex gap-1">
                  {!c.resolved_at ? (
                    <button
                      type="button"
                      data-testid={`comment-resolve-${c.id}`}
                      onClick={() => resolveMutation.mutate(c.id)}
                      className="text-[10px] text-emerald-700 hover:underline"
                    >
                      Lösen
                    </button>
                  ) : (
                    <span className="text-[10px] text-emerald-700">✓ gelöst</span>
                  )}
                  <button
                    type="button"
                    data-testid={`comment-delete-${c.id}`}
                    onClick={() => {
                      if (window.confirm('Kommentar wirklich löschen?')) {
                        deleteMutation.mutate(c.id);
                      }
                    }}
                    className="text-[10px] text-rose-700 hover:underline"
                  >
                    Löschen
                  </button>
                </span>
              </header>
              <p className="whitespace-pre-wrap text-zinc-900">{c.body}</p>

              {c.replies.length > 0 ? (
                <ul className="mt-2 space-y-1 border-l-2 border-zinc-100 pl-2">
                  {c.replies.map((r) => (
                    <li key={r.id} data-testid={`comment-reply-${r.id}`} className="text-[11px]">
                      <span className="text-[10px] text-zinc-500">
                        Token {r.actor_token_id ?? '?'} ·{' '}
                        {r.created_at ? new Date(r.created_at).toLocaleString('de-AT') : '?'}
                      </span>
                      <p className="whitespace-pre-wrap text-zinc-900">{r.body}</p>
                    </li>
                  ))}
                </ul>
              ) : null}

              {!c.resolved_at ? (
                <div className="mt-1">
                  {replyToId === c.id ? (
                    <div className="rounded border border-zinc-100 p-1.5">
                      <textarea
                        value={replyDraft}
                        onChange={(e) => setReplyDraft(e.target.value)}
                        data-testid={`comment-reply-input-${c.id}`}
                        placeholder="Antwort…"
                        rows={2}
                        maxLength={2000}
                        className="w-full rounded border border-zinc-200 bg-zinc-50 p-1 text-[11px]"
                      />
                      <div className="mt-1 flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setReplyToId(null);
                            setReplyDraft('');
                          }}
                          className="rounded px-1.5 py-0.5 text-[10px] text-zinc-600"
                        >
                          Abbrechen
                        </button>
                        <button
                          type="button"
                          data-testid={`comment-reply-submit-${c.id}`}
                          disabled={replyDraft.trim().length === 0 || postMutation.isPending}
                          onClick={() => postMutation.mutate({ body: replyDraft.trim(), parentId: c.id })}
                          className="rounded bg-zinc-900 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
                        >
                          Antworten
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid={`comment-reply-button-${c.id}`}
                      onClick={() => {
                        setReplyToId(c.id);
                        setReplyDraft('');
                      }}
                      className="text-[10px] text-zinc-600 hover:underline"
                    >
                      Antworten
                    </button>
                  )}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
